import { z, type ZodType } from '@customerio/hydra-validation';
import type { Domain as UIAPIDomain } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Domain entity — sending domains for a workspace.
 *
 * Wire shape uses unix-second `created_at`/`updated_at` (with `_at`
 * suffix), unlike most other entities. We keep them as numbers for now
 * since the only consumer (`delivery-mode-banner`) doesn't use the
 * timestamps; introducing Date transforms here would be churn for no
 * benefit. Update this when a date-aware consumer lands.
 */

export type Domain = UIAPIDomain;

/**
 * The fields the only current consumer (`delivery-mode-banner`)
 * actually reads. Asserting these at parse time means a malformed
 * response surfaces as a Zod error instead of silently producing
 * incorrect banner state. Other fields pass through via `looseObject`
 * — declare them here as consumers grow.
 *
 * `verified` is `*bool` in Go (`model.Domain.Verified`) so the wire
 * legitimately emits `null`. We accept the three-state shape directly;
 * consumers use `verified === true` to distinguish "verified" from
 * "not yet verified" / "unknown".
 */
const DomainFromApi =
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  z.looseObject({
    id: z.string(),
    domain: z.string(),
    // Required on the wire; nullish here only to tolerate backend deploy skew —
    // a `*bool` serialization would emit null and must not fail the shared read.
    starter: z.boolean().nullish(),
    verified: z.boolean().nullable(),
  }) as unknown as ZodType<Domain, unknown>;

/**
 * Compatibility projection for starter detection: `starter` is required on
 * the wire, but a skewed backend may omit it, and that absence must be
 * representable to reach the fallback.
 */
export type StarterDomainCandidate = { domain: string; starter?: boolean | null };

// Second signal, not a fallback (see isStarterDomain). `.customerio.build`
// covers every region; `.customerio.dev` is the local sandbox equivalent;
// `.builders.cio.host` is staging. Deletable once the `starter` field is
// confirmed served for all three bases.
const STARTER_DOMAIN_SUFFIXES = ['.customerio.build', '.customerio.dev', '.builders.cio.host'];

/**
 * Whether a domain is a backend-provisioned starter domain (auto-verified,
 * so it must not count as the customer's own verified sending domain).
 * Either signal is enough: the API's `starter` field, or one of the
 * cio-owned bases. Deliberately an OR rather than a field-absent fallback —
 * misclassifying a starter domain as the customer's own would let the
 * builders screen and the onboarding step treat a provisioned domain as a
 * real verified one, and `starter: false` on the sandbox/staging bases is
 * unconfirmed.
 */
export function isStarterDomain(domain: StarterDomainCandidate): boolean {
  if (domain.starter === true) {
    return true;
  }
  const lower = domain.domain.toLowerCase();
  return STARTER_DOMAIN_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}

/** @public */
export const domain = defineEntity<Domain, Domain, void, undefined, 'workspace'>({
  name: 'domain',
  scope: 'workspace',
  pk: (d) => d.id,
  schema: { read: DomainFromApi },
});

export const domainKeys = domain.keys;

export function parseDomain(raw: UIAPIDomain): Domain {
  return domain.parse(raw);
}
